import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { customerCategoriesService } from '../services/customerCategoriesService';
import { CustomerCategory } from '../types/customerCategories';
import { transformEntriesToMap } from '../utils/categoryEntries';
import AddPropertyModal from '../components/AddPropertyModal';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import { 
  Edit, 
  Plus, 
  Trash2, 
  Save, 
  X, 
  AlertCircle, 
  Tag 
} from 'lucide-react';

const CategoryDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { accessToken, setUserAndToken } = useUser();
  const [category, setCategory] = useState<CustomerCategory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [subDeleteState, setSubDeleteState] = useState<{ parentKey: string; subKeyOrIndex: string | number } | null>(null);
  const [newArrayItemKey, setNewArrayItemKey] = useState<string | null>(null);

  // Build array items from server entries with their actual indices
  const getArrayServerItems = (parentKey: string): Array<{ index: number; value: string }> => {
    if (!category?.entries) return [];
    const items = category.entries
      .filter(e => e.key.startsWith(`${parentKey}.`) && /^\d+$/.test(e.key.split('.')[1]))
      .map(e => ({ index: parseInt(e.key.split('.')[1]), value: String(e.value ?? '') }))
      .sort((a, b) => a.index - b.index);
    return items;
  };

  useEffect(() => {
    const fetchCategory = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const cat = await customerCategoriesService.getCustomerCategory(id, accessToken, setUserAndToken);
        setCategory(cat);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch category');
      } finally {
        setLoading(false);
      }
    };

    if (accessToken && id) {
      fetchCategory();
    }
  }, [id, accessToken, setUserAndToken]);

  // Use the same icon as the sidebar for Risk Profiles; enlarged for details page
  const renderFixedIcon = () => <Tag className="w-8 h-8 text-slate-700" />;

  const getCategoryBadgeColor = (categoryName: string) => {
    const name = categoryName.toLowerCase();
    if (name.includes('key') || name.includes('premium')) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    } else if (name.includes('regular') || name.includes('standard')) {
      return 'bg-blue-100 text-blue-800 border-blue-200';
    } else if (name.includes('risky') || name.includes('risk')) {
      return 'bg-red-100 text-red-800 border-red-200';
    } else if (name.includes('new')) {
      return 'bg-green-100 text-green-800 border-green-200';
    }
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const startEditing = (key: string, value: any) => {
    setEditingField(key);
    // Create a shallow copy to avoid mutating original while typing
    if (Array.isArray(value)) {
      setEditValue([...(value as any[])]);
    } else if (typeof value === 'object' && value !== null) {
      setEditValue({ ...(value as Record<string, any>) });
    } else {
      setEditValue(value);
    }
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditValue(null);
    setNewArrayItemKey(null);
  };

  const handleAddProperty = async (property: { key: string; value: any }) => {
    if (!category || !accessToken) return;

    try {
      setSaving(true);
      setError(null);

      let updated: CustomerCategory | undefined;

      if (typeof property.value === 'object' && property.value !== null) {
        // Handle objects and arrays - prepare batch entries
        const batchEntries: Array<{ key: string; value: any }> = [];
        
        if (Array.isArray(property.value)) {
          // Handle arrays - create entries for each array item
          for (let i = 0; i < property.value.length; i++) {
            if (property.value[i] !== '') { // Skip empty items
              batchEntries.push({
                key: `${property.key}.${i}`,
                value: property.value[i]
              });
            }
          }
        } else {
          // Handle objects - create entries for each key-value pair
          for (const [subKey, subValue] of Object.entries(property.value)) {
            if (subKey !== '' && subValue !== '') { // Skip empty keys/values
              batchEntries.push({
                key: convertNestedKeyToApi(property.key, subKey),
                value: subValue
              });
            }
          }
        }
        
        if (batchEntries.length > 0) {
          // Use batch API for all entries (create or update based on ID)
          updated = await customerCategoriesService.createCategoryEntries(
            category.id,
            batchEntries,
            accessToken,
            setUserAndToken
          );
          setCategory(updated);
        }
      } else {
        // Handle primitive values
        updated = await customerCategoriesService.createCategoryEntries(
          category.id,
          [{ key: property.key, value: property.value }],
          accessToken,
          setUserAndToken
        );
        setCategory(updated);
      }
    } catch (err) {
      console.error('Failed to add property:', err);
      setError(err instanceof Error ? err.message : 'Failed to add property');
    } finally {
      setSaving(false);
    }
  };

  const saveField = async () => {
    if (!category || !editingField) return;
    
    // Check for empty keys in new properties
    if (editingField === '' || editingField.trim() === '') {
      setError('Property key cannot be empty. Please enter a key name.');
      return;
    }
    
    // Check for empty keys in object properties
    if (typeof editValue === 'object' && editValue !== null && !Array.isArray(editValue)) {
      const emptyKeys = Object.keys(editValue).filter(key => key.trim() === '');
      if (emptyKeys.length > 0) {
        setError('Object property keys cannot be empty. Please enter key names for all properties.');
        return;
      }
    }
    
    setSaving(true);
    try {
      let updated: CustomerCategory | undefined;
      
      if (editingField === 'order_to_evaluate') {
        // Handle order_to_evaluate as basic field
        updated = await customerCategoriesService.updateCategoryBasics(category.id, { order_to_evaluate: Number(editValue) || 0 }, accessToken, setUserAndToken);
        setCategory(updated);
      } else if (typeof editValue === 'object' && editValue !== null) {
        // Capture current values from uncontrolled inputs
        const currentEditValue = { ...editValue };
        
        // Update object keys from input values
        if (!Array.isArray(editValue)) {
          const inputElements = document.querySelectorAll(`input[data-object-key="${editingField}"]`);
          inputElements.forEach((input: any) => {
            const displayKey = input.value;
            const apiKey = convertToApiKey(displayKey);
            const oldKey = input.dataset.oldKey;
            if (oldKey && oldKey !== apiKey) {
              const value = currentEditValue[oldKey];
              delete currentEditValue[oldKey];
              currentEditValue[apiKey] = value;
            }
          });
        }
        // Handle objects and arrays - prepare batch entries
        const batchEntries: Array<{ key: string; value: any }> = [];
        
        if (Array.isArray(currentEditValue)) {
          // Handle arrays - create entries for each array item
          for (let i = 0; i < currentEditValue.length; i++) {
            if (currentEditValue[i] !== '') { // Skip empty items
              const key = `${editingField}.${i}`;
              // Find existing entry ID if it exists
              const existingEntry = category.entries?.find(entry => entry.key === key);
              batchEntries.push({
                ...(existingEntry?.id && { id: existingEntry.id }),
                key: key,
                value: currentEditValue[i]
              });
            }
          }
        } else {
          // Handle objects - create entries for each key-value pair
          for (const [subKey, subValue] of Object.entries(currentEditValue)) {
            if (subKey !== '' && subValue !== '') { // Skip empty keys/values
              const key = convertNestedKeyToApi(editingField, subKey);
              // Find existing entry ID if it exists
              const existingEntry = category.entries?.find(entry => entry.key === key);
              batchEntries.push({
                ...(existingEntry?.id && { id: existingEntry.id }),
                key: key,
                value: subValue
              });
            }
          }
        }
        
        if (batchEntries.length > 0) {
          // Use batch API for all entries (create or update based on ID)
          updated = await customerCategoriesService.createCategoryEntries(
            category.id,
            batchEntries,
            accessToken,
            setUserAndToken
          );
          setCategory(updated);
        }
      } else {
        // Handle primitive values - find existing entry ID if it exists
        const existingEntry = category.entries?.find(entry => entry.key === editingField);
        const entryData = {
          ...(existingEntry?.id && { id: existingEntry.id }),
          key: editingField,
          value: editValue
        };
        
        updated = await customerCategoriesService.createCategoryEntries(
          category.id,
          [entryData],
          accessToken,
          setUserAndToken
        );
        setCategory(updated);
      }
      
      setEditingField(null);
      setEditValue(null);
    } catch (err) {
      console.error('Failed to save field:', err);
    } finally {
      setSaving(false);
    }
  };

  const updateFormValue = (key: string, value: any) => {
    setEditValue(value);
  };

  const removeFormKey = async (key: string) => {
    setDeleteTarget(key);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!category || !deleteTarget) return;
    
    try {
      setIsDeleting(true);
      const updated = await customerCategoriesService.deleteCategoryEntry(category.id, deleteTarget, accessToken, setUserAndToken);
      setCategory(updated);
      setEditingField(null);
      setEditValue(null);
    } catch (err) {
      console.error('Failed to remove key:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete property');
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
      setShowDeleteModal(false);
    }
  };

  const cancelDelete = () => {
    setDeleteTarget(null);
    setShowDeleteModal(false);
  };

  // Convert display name to API key (e.g., "Main Concerns" -> "main_concerns")
  const convertToApiKey = (displayName: string): string => {
    return displayName
      .toLowerCase()
      .replace(/[^a-z0-9_\s]/g, '') // Remove special characters but keep spaces and underscores
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .trim();
  };

  // Convert nested key to API format (e.g., "main_concern.MC1" -> "main_concern.mc1")
  const convertNestedKeyToApi = (parentKey: string, childKey: string): string => {
    const apiParentKey = convertToApiKey(parentKey);
    const apiChildKey = convertToApiKey(childKey);
    return `${apiParentKey}.${apiChildKey}`;
  };

  // Convert API key to display format (e.g., "ab_cd" -> "Ab Cd")
  const convertApiKeyToDisplay = (apiKey: string): string => {
    return apiKey
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Sync current editValue with latest server state for a given top-level key
  const syncEditValueFromUpdatedCategory = (updatedCategory: CustomerCategory, parentKey: string) => {
    const mapped = transformEntriesToMap(updatedCategory.entries);
    let latest = (mapped as any)[parentKey];
    if (Array.isArray(latest)) {
      // Compact array by removing undefined holes and reindexing
      latest = latest.filter((item: any) => item !== undefined && item !== null);
    }
    setEditValue(latest as any);
  };


  const updatePropertyName = (oldKey: string, newKey: string) => {
    if (!category) return;
    const apiKey = convertToApiKey(newKey);
    
    setCategory(prev => ({
      ...prev!,
      entries: prev?.entries?.map(entry => 
        entry.key === oldKey ? { ...entry, key: apiKey } : entry
      ) || []
    }));
    
    // Update editing field if currently editing this property
    if (editingField === oldKey) {
      setEditingField(apiKey);
    }
  };

  const updatePropertyType = (key: string, newType: 'value' | 'object' | 'array') => {
    let defaultValue: any;
    switch (newType) {
      case 'value': 
        defaultValue = ''; 
        break;
      case 'object': 
        defaultValue = { '': '' }; 
        break;
      case 'array': 
        defaultValue = ['']; 
        break;
    }
    
    setCategory(prev => ({
      ...prev!,
      entries: prev?.entries?.map(entry => 
        entry.key === key ? { ...entry, value: defaultValue } : entry
      ) || []
    }));
    
    // Update editing value if currently editing this property
    if (editingField === key) {
      setEditValue(defaultValue);
    }
  };

  const updateObjectValue = (parentKey: string, childKey: string, value: string) => {
    if (editingField !== parentKey) return;
    setEditValue((prev: any) => ({ ...(prev || {}), [childKey]: value }));
  };

  const updateObjectKey = (parentKey: string, oldChildKey: string, newChildKey: string) => {
    if (editingField !== parentKey) return;
    setEditValue((prev: any) => {
      const newObj = { ...(prev || {}) };
      const value = newObj[oldChildKey];
      delete newObj[oldChildKey];
      newObj[newChildKey] = value;
      return newObj;
    });
  };

  const removeObjectKey = (parentKey: string, childKey: string) => {
    if (editingField !== parentKey) return;
    setEditValue((prev: any) => {
      const copy = { ...(prev || {}) } as Record<string, any>;
      delete copy[childKey];
      return copy;
    });
  };

  const addObjectKey = (parentKey: string, childKey: string) => {
    if (editingField !== parentKey) return;
    setEditValue((prev: any) => {
      // Always add an empty key/value pair at the bottom
      const newObj = { ...(prev || {}), '': '' };
      return newObj;
    });
  };

  const updateArrayValue = (parentKey: string, index: number, value: string) => {
    if (editingField !== parentKey) return;
    setEditValue((prev: any) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      arr[index] = value;
      return arr;
    });
  };

  // Persist a single object sub-entry (by key/index)
  const saveSubEntry = async (parentKey: string, originalSubKey: string, index: number) => {
    if (!category) return;
    try {
      setSaving(true);
      const keyInput = document.getElementById(`key-input-${parentKey}-${index}`) as HTMLInputElement | null;
      const valueInput = document.getElementById(`value-input-${parentKey}-${index}`) as HTMLInputElement | null;
      const displayKey = keyInput?.value ?? '';
      // Avoid double-transform; let convertNestedKeyToApi handle conversion once
      const newKeyPath = convertNestedKeyToApi(parentKey, displayKey);
      const value = valueInput?.value ?? '';

      const originalKeyPath = `${parentKey}.${originalSubKey}`;
      const existingEntry = category.entries?.find(e => e.key === originalKeyPath);
      const payload = [{ ...(existingEntry?.id && { id: existingEntry.id }), key: newKeyPath, value }];

      const updated = await customerCategoriesService.createCategoryEntries(
        category.id,
        payload,
        accessToken,
        setUserAndToken
      );
      setCategory(updated);
      // Refresh edit value from latest server state
      syncEditValueFromUpdatedCategory(updated, parentKey);
      // Exit editing mode to return UI to pre-edit state
      setEditingField(null);
      setEditValue(null);
    } finally {
      setSaving(false);
    }
  };

  // Persist a single array item (by index)
  const saveArrayItem = async (parentKey: string, displayIndex: number) => {
    if (!category) return;
    try {
      setSaving(true);
      const inputId = displayIndex === -1 ? `value-input-${parentKey}-new` : `value-input-${parentKey}-${displayIndex}`;
      const valueInput = document.getElementById(inputId) as HTMLInputElement | null;
      const value = valueInput?.value ?? '';
      // For new items, determine next available server index
      const existingIndices = category.entries
        ?.filter(e => e.key.startsWith(`${parentKey}.`) && /^\d+$/.test(e.key.split('.')[1]))
        .map(e => parseInt(e.key.split('.')[1])) || [];
      const nextIndex = existingIndices.length === 0 ? 0 : Math.max(...existingIndices) + 1;
      const serverIndex = displayIndex === -1 ? nextIndex : displayIndex;

      const keyPath = `${parentKey}.${serverIndex}`;
      const existingEntry = category.entries?.find(e => e.key === keyPath);
      const payload = [{ ...(existingEntry?.id && { id: existingEntry.id }), key: keyPath, value }];
      const updated = await customerCategoriesService.createCategoryEntries(
        category.id,
        payload,
        accessToken,
        setUserAndToken
      );
      setCategory(updated);
      // Refresh edit value from latest server state
      syncEditValueFromUpdatedCategory(updated, parentKey);
      // Exit editing mode to return UI to pre-edit state
      setEditingField(null);
      setEditValue(null);
      setNewArrayItemKey(null);
    } finally {
      setSaving(false);
    }
  };

  // Delete a single sub-entry immediately
  const deleteSubEntry = async (parentKey: string, subKeyOrIndex: string | number) => {
    if (!category) return;
    try {
      setIsDeleting(true);
      const keyPath = `${parentKey}.${subKeyOrIndex}`;
      const updated = await customerCategoriesService.deleteCategoryEntry(
        category.id,
        keyPath,
        accessToken,
        setUserAndToken
      );
      setCategory(updated);
      // Refresh edit value from latest server state
      syncEditValueFromUpdatedCategory(updated, parentKey);
      // Exit editing mode to return UI to pre-edit state
      setEditingField(null);
      setEditValue(null);
    } catch (err) {
      console.error('Failed to delete sub entry:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete sub entry');
    } finally {
      setIsDeleting(false);
    }
  };

  const requestDeleteSubEntry = (parentKey: string, subKeyOrIndex: string | number) => {
    setSubDeleteState({ parentKey, subKeyOrIndex });
  };

  const cancelDeleteSubEntry = () => {
    setSubDeleteState(null);
  };

  const confirmDeleteSubEntry = async () => {
    if (!subDeleteState) return;
    const { parentKey, subKeyOrIndex } = subDeleteState;
    await deleteSubEntry(parentKey, subKeyOrIndex);
    setSubDeleteState(null);
  };

  const addArrayItem = (parentKey: string) => {
    if (editingField !== parentKey) return;
    setNewArrayItemKey(parentKey);
  };

  const cancelNewArrayItem = (parentKey: string) => {
    if (newArrayItemKey === parentKey) {
      setNewArrayItemKey(null);
    }
  };

  const removeArrayItem = (parentKey: string, index: number) => {
    if (editingField !== parentKey) return;
    setEditValue((prev: any) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      return arr.filter((_: any, i: number) => i !== index);
    });
  };

  const renderValue = (key: string, value: any) => {
    if (Array.isArray(value)) {
      // Filter out undefined/null values for display
      const filteredValue = value.filter((item: any) => item !== undefined && item !== null && item !== '');
      return (
        <div className="space-y-2">
          <ul className="space-y-1">
            {filteredValue.map((item: string, index: number) => (
              <li key={index} className="flex items-start">
                <span className="text-gray-500 text-sm mr-2">•</span>
                <span className="text-sm text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      );
    } else if (typeof value === 'object' && value !== null) {
      return (
        <div className="space-y-2">
          <div className="overflow-hidden">
            <table className="min-w-full">
              <tbody>
                {Object.entries(value).map(([subKey, subValue]) => (
                  <tr key={subKey} className="border-b border-gray-100 last:border-b-0">
                    <td className="py-2 pr-4 text-sm text-gray-600 font-medium align-top w-1/3">
                      {subKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                    </td>
                    <td className="py-2 text-sm text-gray-700 align-top">
                      {subValue as string}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    } else {
      return (
        <div className="text-sm text-gray-700">
          {value}
        </div>
      );
    }
  };

  const renderEditField = (key: string, value: any) => {
    const currentValue = key === editingField ? editValue : value;
    // Check if this is a new property by looking at the original entries array
    const isNewProperty = category?.entries?.some(entry => 
      entry.id?.startsWith('temp-') && entry.key === key
    ) || false;
    
    // Special handling for order_to_evaluate
    if (key === 'order_to_evaluate') {
      return (
        <input
          type="number"
          value={currentValue}
          onChange={(e) => updateFormValue(key, parseInt(e.target.value) || 0)}
          className="w-full border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100"
          placeholder="Enter evaluation order"
        />
      );
    }

    // For new properties, show name and type selectors
    if (isNewProperty) {
      const currentType = Array.isArray(currentValue) ? 'array' : typeof currentValue === 'object' && currentValue !== null ? 'object' : 'value';
      
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className="block text-xs text-slate-600 mb-1">Property Key</label>
              <input
                type="text"
                value={key.replace(/^(property_|temp-)/, '')}
                onChange={(e) => updatePropertyName(key, e.target.value)}
                className="w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                placeholder="Enter property key"
              />
            </div>
            <div className="w-32">
              <label className="block text-xs text-slate-600 mb-1">Value Type</label>
              <select
                value={currentType}
                onChange={(e) => updatePropertyType(key, e.target.value as 'value' | 'object' | 'array')}
                className="w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
              >
                <option value="value">Value</option>
                <option value="object">Object</option>
                <option value="array">Array</option>
              </select>
            </div>
          </div>
          
          {/* Value editing based on type */}
          {currentType === 'value' && (
            <div>
              <label className="block text-xs text-slate-600 mb-1">Value</label>
              <input
                type="text"
                value={currentValue}
                onChange={(e) => updateFormValue(key, e.target.value)}
                className="w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                placeholder="Enter value"
              />
            </div>
          )}
          
          {currentType === 'object' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-xs text-slate-600">Object Properties</label>
                <AddObjectKeyForm parentKey={key} onAdd={addObjectKey} />
              </div>
              {Object.entries(currentValue).map(([subKey, subValue], index) => {
                const hasExisting = !!category?.entries?.some(e => e.key === `${key}.${subKey}`);
                return (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="text"
                      defaultValue={convertApiKeyToDisplay(subKey)}
                      id={`key-input-${key}-${index}`}
                      data-object-key={editingField}
                      data-old-key={subKey}
                      className="w-1/3 border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                      placeholder="Key"
                    />
                    <input
                      type="text"
                      defaultValue={subValue as string}
                      id={`value-input-${key}-${index}`}
                      className="flex-1 border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                      placeholder="Value"
                    />
                    <button
                      onClick={() => saveSubEntry(key, subKey, index)}
                      className="text-green-600 hover:text-green-700 p-1 flex items-center gap-1"
                      title="Save this item"
                    >
                      <Save className="w-4 h-4" />
                    </button>
                    {hasExisting && subKey !== '' && (
                      <button
                        onClick={() => requestDeleteSubEntry(key, subKey)}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Remove property"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          
          {currentType === 'array' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-xs text-slate-600">Array Items</label>
                <button
                  onClick={() => addArrayItem(key)}
                  className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" />
                  Add Item
                </button>
              </div>
          {(() => {
            const serverItems = getArrayServerItems(key);
            const rows: React.ReactNode[] = [];
            const shouldShowNew = newArrayItemKey === key;
            serverItems.forEach(({ index: serverIndex, value }) => {
              rows.push(
                <div key={serverIndex} className="flex items-center gap-2">
                  <input
                    type="text"
                    defaultValue={value}
                    id={`value-input-${key}-${serverIndex}`}
                    className="flex-1 border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                    placeholder="Array item"
                  />
                  <button
                    onClick={() => saveArrayItem(key, serverIndex)}
                    className="text-green-600 hover:text-green-700 p-1 flex items-center gap-1"
                    title="Save this item"
                  >
                    <Save className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => requestDeleteSubEntry(key, serverIndex)}
                    className="text-red-500 hover:text-red-700 p-1"
                    title="Remove item"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            });
            if (shouldShowNew) {
              rows.push(
                <div key={`new`} className="flex items-center gap-2">
                  <input
                    type="text"
                    defaultValue=""
                    id={`value-input-${key}-new`}
                    className="flex-1 border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                    placeholder="Array item"
                  />
                  <button
                    onClick={() => saveArrayItem(key, -1)}
                    className="text-green-600 hover:text-green-700 p-1 flex items-center gap-1"
                    title="Save this item"
                  >
                    <Save className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => cancelNewArrayItem(key)}
                    className="text-red-500 hover:text-red-700 p-1"
                    title="Remove item"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            }
            return rows;
          })()}
            </div>
          )}
        </div>
      );
    }
    
    return renderValueContent(key, currentValue);
  };

  const renderValueContent = (key: string, currentValue: any) => {
    if (Array.isArray(currentValue)) {
      return (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">Array Items</span>
            <button
              onClick={() => addArrayItem(key)}
              className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
            >
              <Plus className="w-3 h-3" />
              Add Item
            </button>
          </div>
          {(() => {
            const serverItems = getArrayServerItems(key);
            const rows: React.ReactNode[] = [];
            const shouldShowNew = newArrayItemKey === key;
            serverItems.forEach(({ index: serverIndex, value }) => {
              rows.push(
                <div key={serverIndex} className="flex items-center gap-2">
                  <input
                    type="text"
                    defaultValue={value}
                    id={`value-input-${key}-${serverIndex}`}
                    className="flex-1 border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    placeholder="Array item"
                  />
                  <button
                    onClick={() => saveArrayItem(key, serverIndex)}
                    className="text-green-600 hover:text-green-700 p-1 flex items-center gap-1"
                    title="Save this item"
                  >
                    <Save className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => requestDeleteSubEntry(key, serverIndex)}
                    className="text-red-500 hover:text-red-700 p-1"
                    title="Remove item"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            });
            if (shouldShowNew) {
              rows.push(
                <div key={`new`} className="flex items-center gap-2">
                  <input
                    type="text"
                    defaultValue=""
                    id={`value-input-${key}-new`}
                    className="flex-1 border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    placeholder="Array item"
                  />
                  <button
                    onClick={() => saveArrayItem(key, -1)}
                    className="text-green-600 hover:text-green-700 p-1 flex items-center gap-1"
                    title="Save this item"
                  >
                    <Save className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => cancelNewArrayItem(key)}
                    className="text-red-500 hover:text-red-700 p-1"
                    title="Remove item"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            }
            return rows;
          })()}
        </div>
      );
    } else if (typeof currentValue === 'object' && currentValue !== null) {
      return (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">Object Properties</span>
            <AddObjectKeyForm parentKey={key} onAdd={addObjectKey} />
          </div>
          {Object.entries(currentValue).map(([subKey, subValue], index) => {
            const hasExisting = !!category?.entries?.some(e => e.key === `${key}.${subKey}`);
            return (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  defaultValue={convertApiKeyToDisplay(subKey)}
                  id={`key-input-${key}-${index}`}
                  data-object-key={editingField}
                  data-old-key={subKey}
                  className="w-1/3 border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  placeholder="Key"
                />
                <input
                  type="text"
                  defaultValue={subValue as string}
                  id={`value-input-${key}-${index}`}
                  className="flex-1 border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  placeholder="Value"
                />
                <button
                  onClick={() => saveSubEntry(key, subKey, index)}
                  className="text-green-600 hover:text-green-700 p-1 flex items-center gap-1"
                  title="Save this item"
                >
                  <Save className="w-4 h-4" />
                </button>
                {subKey === '' ? (
                  <button
                    onClick={() => removeObjectKey(key, '')}
                    className="text-red-500 hover:text-red-700 p-1"
                    title="Remove property"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                ) : (
                  hasExisting && (
                    <button
                      onClick={() => requestDeleteSubEntry(key, subKey)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Remove property"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )
                )}
              </div>
            );
          })}
        </div>
      );
    } else {
      return (
        <input
          type="text"
          value={currentValue ?? ''}
          onChange={(e) => updateFormValue(key, e.target.value)}
          className="w-full border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100"
          placeholder="Enter value"
        />
      );
    }
  };

  // Helper components

  const AddObjectKeyForm: React.FC<{ parentKey: string; onAdd: (parentKey: string, childKey: string) => void }> = ({ parentKey, onAdd }) => {
    const handleAdd = () => {
      // Add with blank key - user will fill it in
      onAdd(parentKey, '');
    };

    return (
      <button
        onClick={handleAdd}
        className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
      >
        <Plus className="w-3 h-3" />
        Add
      </button>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading category details...</p>
        </div>
      </div>
    );
  }

  if (error || !category) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center p-6 bg-red-50 border border-red-200 rounded-lg shadow-sm">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-red-800 mb-2">Error Loading Category</h3>
          <p className="text-red-700">{error || 'Category not found'}</p>
          <button
            onClick={() => navigate('/customer-categories')}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
          >
            Back to Categories
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-3">
            {renderFixedIcon()}
            <div>
              <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Afacad, sans-serif' }}>
                {category.name}
              </h1>
              <p className="text-gray-600">{category.description}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`px-4 py-2 rounded-full text-sm font-medium border whitespace-nowrap ${getCategoryBadgeColor(category.name)}`}>
            Evaluation Order: {transformEntriesToMap(category.entries).order_to_evaluate}
          </span>
          {category.is_default && (
            <span className="px-4 py-2 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
              Default
            </span>
          )}
        </div>
      </div>

      {/* Properties Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-900">Properties</h2>
          <button
            onClick={() => setShowAddPropertyModal(true)}
            className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
          >
            <Plus className="w-4 h-4" />
            Add Property
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          {Object.entries(transformEntriesToMap(category.entries)).map(([key, value]) => {
            
            const isEditing = editingField === key;
            const isNewProperty = category?.entries?.some(entry => 
              entry.id?.startsWith('temp-') && entry.key === key
            ) || false;
            
            return (
              <div key={key} className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-slate-900">
                    {isNewProperty ? 'New Property' : (key === 'order_to_evaluate' ? 'Evaluation Order' : key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))}
                  </h3>
                  <div className="flex items-center space-x-2">
                    {!isEditing ? (
                      <>
                        <button
                          onClick={() => startEditing(key, value)}
                          className="p-1 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
                          title="Edit this property"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => removeFormKey(key)}
                          className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                          title="Remove this property"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        {!(typeof value === 'object' && value !== null) && (
                          <button
                            onClick={saveField}
                            disabled={saving}
                            className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                            title="Save changes"
                          >
                            <Save className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={cancelEditing}
                          className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                          title="Cancel editing"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="mt-3">
                  {isEditing ? renderEditField(key, value) : renderValue(key, value)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Add Property Modal */}
      <AddPropertyModal
        isOpen={showAddPropertyModal}
        onClose={() => setShowAddPropertyModal(false)}
        onSave={handleAddProperty}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Property"
        message="Are you sure you want to delete this property? This action cannot be undone."
        itemName={deleteTarget ? deleteTarget.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : undefined}
        isLoading={isDeleting}
      />

      {/* Sub-entry Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={!!subDeleteState}
        onClose={cancelDeleteSubEntry}
        onConfirm={confirmDeleteSubEntry}
        title="Delete Sub-entry"
        message="Are you sure you want to delete this item? This action cannot be undone."
        itemName={subDeleteState ? `${subDeleteState.parentKey}.${String(subDeleteState.subKeyOrIndex)}`.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : undefined}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default CategoryDetails;
